import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from './ui/button';
import { ArrowLeft, Filter, ChevronDown, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { VideoCard } from '../model/home';
import { getExporeList } from '../api/expore';
import { Subject } from '../model/video';
import { subjectColors } from '../consts/subject';
import { CommonVideoCard } from './homepage/CommonVideoCard';
import type { SubjectColor } from './homepage/MyVideoCard';
import { videoCardToCommon } from '../utils/VideoUtils';

interface ExplorePageProps {
  userid?: string | null;
  onVideoClick: (videoId: string) => void;
  onBackToMyVideos: () => void;
}

export function ExplorePage({
  onVideoClick,
  userid,
  onBackToMyVideos,
}: ExplorePageProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef<boolean>(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const lastRef = useRef<string>('');
  const [last, setLast] = useState<string>('');
  const [selectedSubject, setSelectedSubject] =
    useState<Subject>('All Subjects');
  const selectedSubjectRef = useRef<Subject>('All Subjects');
  const sortOrderRef = useRef<'popular' | 'newest' | 'oldest'>('popular');
  const [sortOrder, setSortOrder] = useState<'popular' | 'newest' | 'oldest'>(
    'popular',
  );
  const [videoList, setVideoList] = useState<VideoCard[]>([]);

  // Sort subjects alphabetically
  const sortedSubjects = Object.keys(subjectColors) as Subject[];

  const fetchList = async (ref?: string) => {
    if (isLoadingRef.current) {
      return;
    }
    setIsLoading(true);
    isLoadingRef.current = true;

    // if (userid) {
    try {
      console.log('isfist:', ref, lastRef.current);
      const res = await getExporeList(
        sortOrderRef.current,
        selectedSubjectRef.current == 'All Subjects'
          ? ''
          : selectedSubjectRef.current,
        ref ? ref : void 0,
      );
      if (ref) {
        setVideoList((prev) => prev.concat(res.cards || []));
      } else {
        setVideoList(res.cards || []);
      }
      console.log(res.ref_id);
      setLast(res.ref_id);
      if (res.ref_id) {
        lastRef.current = res.ref_id;
      }
    } catch (e) {
      toast.error(e.message || 'request fail');
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
    // }
  };
  useEffect(() => {
    (async () => {
      await fetchList('');
      if (lastRef.current) {
        await fetchList(lastRef.current);
      } else {
        setLast('');
      }
      if (lastRef.current) {
        await fetchList(lastRef.current);
      } else {
        setLast('');
      }
    })();
  }, [sortOrder, selectedSubject]);
  const updateList = (e) => {
    try {
      console.log(
        scrollAreaRef.current.scrollTop,
        scrollAreaRef.current.clientHeight,
        scrollAreaRef.current.scrollHeight,
      );
      if (
        scrollAreaRef.current.scrollTop + scrollAreaRef.current.clientHeight >=
        scrollAreaRef.current.scrollHeight
      ) {
        fetchList(lastRef.current);
      }
    } catch (e) {
      toast.error(e.message || 'get history error');
    }
  };
  useEffect(() => {
    setTimeout(() => {
      if (scrollAreaRef.current) {
        scrollAreaRef.current.addEventListener('scroll', updateList);
      }
    }, 50);
    return () => {
      if (scrollAreaRef.current) {
        scrollAreaRef.current.removeEventListener('scroll', updateList);
      }
    };
  }, []);
  const sortMap: Record<'popular' | 'newest' | 'oldest', string> = {
    popular: 'Most Popular',
    oldest: 'Oldest First',
    newest: 'Newest First',
  };
  const handleSort = async (stype: 'popular' | 'newest' | 'oldest') => {
    setSortOrder(stype);
    sortOrderRef.current = stype;
  };

  return (
    <div
      ref={scrollAreaRef}
      style={{ height: '100vh', overflow: 'scroll' }}
      className="w-full"
    >
      <div className="min-h-screen pt-24 pb-12 px-18 bg-white">
        <div className="max-w-[1440px] mx-auto">
          <div className="mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
            <h1 className="mb-2 font-alipuhui-b">Explore Popular Clips</h1>
            <p className="text-muted-foreground">
              Discover and learn from videos created by the community
            </p>
          </div>

          {/* Search and Filter Controls */}
          <div className="flex flex-wrap gap-4 mb-8 justify-between animate-in fade-in slide-in-from-top-4 duration-500 delay-100">
            {/* Left side - Subject Filter */}
            <div className="flex gap-4">
              {isLoading ? (
                <DropdownMenu open={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-xl bg-white">
                      <Filter className="w-4 h-4 mr-2" />
                      {selectedSubject}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {sortedSubjects.map((subject) => (
                      <DropdownMenuItem
                        key={subject}
                        onClick={() => {
                          setSelectedSubject(subject);
                          selectedSubjectRef.current = subject;
                        }}
                      >
                        {subject}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-xl bg-white">
                      <Filter className="w-4 h-4 mr-2" />
                      {selectedSubject}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {sortedSubjects.map((subject) => (
                      <DropdownMenuItem
                        key={subject}
                        onClick={() => {
                          setSelectedSubject(subject);
                          selectedSubjectRef.current = subject;
                        }}
                      >
                        {subject}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Right side - Sort and Search */}
            <div className="flex gap-4">
              {/* Sort Order */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-xl bg-white">
                    {sortMap[sortOrder]}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleSort('popular')}>
                    Most Popular
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('newest')}>
                    Newest First
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('oldest')}>
                    Oldest First
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Search */}
              {/* <div className="relative w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name"
                className="pl-10 bg-white border-border rounded-xl"
              />
            </div> */}
            </div>
          </div>
          {(videoList || []).length === 0 ? (
            isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-12 h-12 text-black animate-spin" />
              </div>
            ) : (
              <div className="text-center py-16 animate-in fade-in duration-500">
                <div className="mb-4 flex justify-center">
                  <img
                    src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-11-10/312521f1-74a7-4d0d-b3e3-dd14dd7f9634/6edd4dcb-3619-4d12-bfa3-34f4ae2144b9.png"
                    alt="No videos"
                    style={{ width: '140px', height: '140px' }}
                  />
                </div>
                <h3 className="mb-2 text-black">No videos found</h3>
                <p className="text-muted-foreground">
                  {selectedSubject !== 'All Subjects'
                    ? 'Try adjusting your filters'
                    : 'Start generating your first video explanation!'}
                </p>
              </div>
            )
          ) : (
            <>
              <div className="flex items-center justify-start gap-4 flex-wrap video-list">
                {(videoList || []).map((video, index) => (
                  <CommonVideoCard
                    key={`${video.video_id}_${index}_${video.video_url}`}
                    video={videoCardToCommon(video)}
                    index={index}
                    subjectColorMap={
                      subjectColors as Record<string, SubjectColor>
                    }
                    onVideoClick={onVideoClick}
                  />
                ))}
              </div>
              {last && (
                <div className="text-center mt-6 animate-in fade-in duration-500">
                  <Button
                    onClick={() => fetchList(lastRef.current)}
                    variant="outline"
                    className="rounded-xl"
                  >
                    Load More
                  </Button>
                </div>
              )}
            </>
          )}

          <button
            onClick={() => navigate('/')}
            className="fixed bottom-8 right-8 bg-primary text-black px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
